import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Factory,
  FlaskConical,
  Layers,
  Sparkles,
  BookOpen,
  Database,
  ScrollText,
  TrendingDown,
  Droplets,
  BrainCircuit,
  BookMarked,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
// import { useHasNewChangelog } from "@/hooks/use-updates";

const OVERVIEW_ITEMS = [
  { to: "/app", label: "Overview", icon: LayoutDashboard },
];

const PRODUCTION_ITEMS = [
  { to: "/app/production", label: "Dashboard", icon: Factory },
  { to: "/app/materials", label: "Materials", icon: Layers },
  { to: "/app/formulations", label: "Formulations", icon: ScrollText },
  { to: "/app/waste", label: "Yield & Waste", icon: TrendingDown },
];

const QUALITY_ITEMS = [
  { to: "/app/quality", label: "Control Charts", icon: FlaskConical },
  { to: "/app/raw-materials", label: "Raw Materials", icon: Droplets },
  { to: "/app/insights", label: "Insights", icon: Sparkles },
];

const TOOL_ITEMS = [
  { to: "/app/analyst", label: "Data Analyst", icon: BrainCircuit },
  { to: "/app/kb", label: "Knowledge Base", icon: BookMarked },
  { to: "/app/guide", label: "User Guide", icon: BookOpen },
  { to: "/app/explore", label: "Data Explorer", icon: Database },
];

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: React.ReactNode;
}

function NavSection({
  label,
  items,
  onClose,
  collapsed,
}: {
  label: string;
  items: NavItem[];
  onClose: () => void;
  collapsed: boolean;
}) {
  return (
    <div className="mb-2">
      {!collapsed && (
        <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
          {label}
        </p>
      )}
      {collapsed && <div className="pt-2" />}
      <div className="space-y-0.5">
        {items.map(({ to, label: itemLabel, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/app"}
            onClick={onClose}
            title={collapsed ? itemLabel : undefined}
            className={({ isActive }) =>
              `flex items-center ${collapsed ? "justify-center" : "gap-3"} rounded-lg ${
                collapsed ? "px-2" : "px-3"
              } py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
              }`
            }
          >
            <span className="relative shrink-0">
              <Icon className="h-4 w-4" />
              {collapsed && badge}
            </span>
            {!collapsed && itemLabel}
            {!collapsed && badge}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const COLLAPSED_KEY = "plastiq-sidebar-collapsed";

export function Sidebar({ open, onClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const toolItems = TOOL_ITEMS;

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col border-r border-sidebar-border bg-sidebar
          transition-all duration-200 ease-in-out
          lg:static lg:translate-x-0
          ${open ? "translate-x-0 w-64" : "-translate-x-full w-64"}
          ${collapsed ? "lg:w-14" : "lg:w-64"}
        `}
      >
        {/* Logo / brand */}
        <div className={`flex h-14 items-center ${collapsed ? "justify-center px-2" : "justify-between px-6"}`}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-bold shrink-0">
                P
              </div>
              <span className="text-lg font-semibold text-sidebar-foreground">
                PlastiQ AI
              </span>
            </div>
          )}
          {collapsed && (
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-bold">
              P
            </div>
          )}
          <button
            onClick={onClose}
            className="lg:hidden text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav items */}
        <nav className={`flex-1 overflow-y-auto ${collapsed ? "px-1.5" : "px-3"} py-4`}>
          <NavSection label="Overview" items={OVERVIEW_ITEMS} onClose={onClose} collapsed={collapsed} />
          <NavSection label="Production" items={PRODUCTION_ITEMS} onClose={onClose} collapsed={collapsed} />
          <NavSection label="Quality" items={QUALITY_ITEMS} onClose={onClose} collapsed={collapsed} />
          <NavSection label="Tools" items={toolItems} onClose={onClose} collapsed={collapsed} />
        </nav>

        {/* Footer with collapse toggle */}
        <div className={`border-t border-sidebar-border ${collapsed ? "p-2" : "p-4"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
            {!collapsed && (
              <p className="text-xs text-muted-foreground">
                PlastiQ AI v0.1
              </p>
            )}
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
